"use client";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import PopOver from "../../../components/atoms/popOver";
import ButtonComponent from "../../../components/atoms/button";

import { constants } from "../../../constants/pageConstants/pdpConstants";
import isEmpty from "lodash.isempty";
import { CONTINUE_SHOPPING, COUNTRIES, CURRENCY_ID } from "../../../constants/common/common";
import LocationLock from "../../../components/molecules/locationLock";
import { LAST_VISITED_ADDRESS, LAST_VISITED_PINCODE } from "../../../constants/storage";
import CookieUtil from "../../../utils/storage/cookieUtil";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { setSelectedDomesticLocation } from "../../../store/slices/locationLock";
import {
  requestToFetchBlockedDatesAction,
  requestToFetchDeliveryDatesAction,
  requestToFetchInternationalShippingMethod,
} from "../../../store/slices/common/shippingDateAndSlotSelection";
import useAnalytics from "../../../analytics/useAnalytics";
import { GA4_EVENTS } from "../../../analytics/gaConstant";
import { isSameSkuProduct } from "../../../utils/pdp/nonDeliverable";

/**
 * Component that displays a Location lock section.
 * @returns {JSX.Element} The rendered LocationLock component.
 */
const DesktopLocationLockSection = ({ isLocationError, fullProductDetails }) => {
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const [selectedCountryData, setSelectedCountryData] = useState({});

  const lastVisitedAddress = CookieUtil.getCookie(LAST_VISITED_ADDRESS);
  const lastVisitedPincode = CookieUtil.getCookie(LAST_VISITED_PINCODE);
  const { selectedDomesticLocation, clientCurrentClickedItem, catalogId, selectedIntLocation } = useSelector(
    (state) => ({
      selectedDomesticLocation: state.locationLock.selectedDomesticLocation,
      clientCurrentClickedItem: state?.pdp?.clientCurrentClickedItem,
      catalogId: state?.appConfig?.catalogId,
      selectedIntLocation: state?.locationLock?.selectedIntLocation,
    }),
    shallowEqual,
  );

  useEffect(() => {
    // Delivery address initialization from localStorage of Domestic
    if (lastVisitedAddress && lastVisitedPincode) {
      dispatch(
        setSelectedDomesticLocation({
          pincode: lastVisitedPincode,
          address: lastVisitedAddress,
        }),
      );
      trackEvent(GA4_EVENTS?.PINCODE_AVAILABLE, { pincode: lastVisitedPincode }, ["ga4"]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { address, pincode } = selectedDomesticLocation;
    const { pincode: pincodeInt } = selectedIntLocation;

    const { assocProductDetails } = clientCurrentClickedItem;
    let productIds = clientCurrentClickedItem?.productId;
    if (isSameSkuProduct(assocProductDetails)) {
      const assocProductId = assocProductDetails?.substituteProduct?.productId;
      if (assocProductId) {
        productIds = `${productIds},${assocProductId}`;
      }
    }

    if (address && pincode && catalogId === COUNTRIES.INDIA) {
      dispatch(
        requestToFetchDeliveryDatesAction({
          productId: productIds,
          pinCode: pincode,
          currencyUomId: CURRENCY_ID,
          catalogId: clientCurrentClickedItem?.catalogId,
          countryGeoId: clientCurrentClickedItem?.countryGeoId,
        }),
      );
      dispatch(
        requestToFetchBlockedDatesAction({
          productId: clientCurrentClickedItem?.productId,
          pinCode: pincode,
        }),
      );
    } else if (pincodeInt && catalogId !== COUNTRIES.INDIA) {
      dispatch(
        requestToFetchInternationalShippingMethod({
          productId: productIds,
          city: pincodeInt,
        }),
      );
    }
  }, [selectedDomesticLocation, selectedIntLocation]);

  /**
   * Function to handle the visibility of the modal.
   * @returns {void} No return value.
   */
  const onHandleModalVisibility = (item = {}) => {
    if (item?.catelogID !== COUNTRIES.INDIA) {
      setSelectedCountryData(item);
    }
  };

  /**
   * Function to handle the "Continue Shopping" action.
   *
   * @returns {void} No return value.
   */
  const onHandleContinueShopping = () => {
    setSelectedCountryData({});
    window.location.href = `/${selectedCountryData?.catelogID}/gifts-lp?promo=mob_countryList`;
  };

  return (
    <div className="relative flex" id="locationLock" data-testid="locationLock">
      <PopOver
        isVisible={!isEmpty(selectedCountryData)}
        onTogglePopover={() => onHandleModalVisibility({})}
        isBackdrop={true}
        isShowCancelIcon={true}
        classes="w-[40%] h-[20%] items-center top-[40%] left-[30%]"
      >
        <div className="p-32">
          <div className="p-32 text-center">
            <div>{constants.COUNTRY_CHANGE}</div>
            <div>
              {constants.REDIRECTED_TO} <span className="font-bold">{selectedCountryData?.countryName}</span>{" "}
              {constants.CATALOG}
            </div>
          </div>
          <ButtonComponent
            onClick={onHandleContinueShopping}
            size="medium"
            buttonVariant="iconWithTextFilled"
            subIcon={`/icons/arrow-right.svg`}
            label={CONTINUE_SHOPPING}
            backGroundColor="bg-[url('/icons/login-bg.svg')]"
          />
        </div>
      </PopOver>
      <LocationLock
        fullProductDetails={fullProductDetails}
        onCountrySelection={onHandleModalVisibility}
        isLocationError={isLocationError}
        messageText={constants.PRODUCT_CANNOT_DELIVERED}
        isFromPDP={true}
        messageClass="w-max pe-16 -ml-[79px]"
      />
    </div>
  );
};

DesktopLocationLockSection.propTypes = {
  fullProductDetails: PropTypes.object.isRequired,
  isLocationError: PropTypes.bool,
};

export default DesktopLocationLockSection;
